import { useState } from 'react';
import { FileText } from 'lucide-react';
import { DataTable, EmptyState, Pagination, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { Section } from '@vnetwork/ui/layout';
import { useResellerInvoices, type ResellerInvoiceRow } from '@/api/resellers';
import { EntityLink } from '@/components/entity-link';
import { paginationView } from '@/pages/customers/pagination-summary';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { formatDate, formatMoney } from '@/lib/format';

const PAGE_SIZE = 20;

function invoiceStatusTone(status: string) {
  switch (status) {
    case 'paid':             return 'success' as const;
    case 'approved':         return 'info'    as const;
    case 'waiting-confirm':  return 'warning' as const;
    case 'overdue':          return 'danger'  as const;
    case 'cancelled':        return 'danger'  as const;
    default:                 return 'neutral' as const;
  }
}

function humanize(s: string) {
  if (!s) return '—';
  return s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, ' ');
}

const COLUMNS: ColumnDef<ResellerInvoiceRow, unknown>[] = [
  {
    id: 'invoice_id',
    header: 'Invoice',
    cell: ({ row }) => (
      <EntityLink
        to={`/invoices/${row.original.invoice_id}`}
        id={row.original.invoice_id}
        name={`Invoice #${row.original.invoice_id}`}
      />
    ),
  },
  {
    id: 'customer_name',
    header: 'Customer',
    cell: ({ row }) => (
      <EntityLink
        to={`/customers/${row.original.customer_id}`}
        id={row.original.customer_id}
        name={row.original.customer_name}
      />
    ),
  },
  {
    id: 'invoice_status',
    header: 'Status',
    cell: ({ row }) => (
      <StatusPill tone={invoiceStatusTone(row.original.invoice_status)}>
        {humanize(row.original.invoice_status)}
      </StatusPill>
    ),
  },
  {
    id: 'invoice_total',
    header: () => <div style={{ textAlign: 'right' }}>Total</div>,
    cell: ({ row }) => (
      <div style={{ textAlign: 'right', fontWeight: 'var(--vn-font-weight-semibold)' }}>
        {formatMoney(row.original.invoice_total)}
      </div>
    ),
  },
  {
    id: 'due_date',
    header: 'Due Date',
    cell: ({ row }) => formatDate(row.original.due_date ?? undefined),
  },
  {
    id: 'created_at',
    header: 'Created',
    cell: ({ row }) => formatDate(row.original.created_at),
  },
];

/** Invoices tab — list of invoices from reseller's linked customers. */
export function ResellerInvoicesTab({ resellerId }: { resellerId: number }) {
  const [page, setPage] = useState(1);

  const { data, isLoading, isError, refetch } = useResellerInvoices(resellerId, {
    page,
    page_size: PAGE_SIZE,
  });

  const rows = data?.results ?? [];
  const total = data?.pagination?.total ?? rows.length;
  const pageCount = data?.pagination?.total_pages ?? 1;

  return (
    <Section title="Invoices">
      {isError ? (
        <TabError title="Failed to load invoices" onRetry={() => refetch()} />
      ) : isLoading ? (
        <TabLoading rows={5} />
      ) : (
        <DataTable
          columns={COLUMNS}
          data={rows}
          getRowId={(r) => String(r.invoice_id)}
          emptyState={
            <EmptyState
              icon={<FileText size={24} />}
              title="No invoices"
              description="Invoices from linked customers will appear here."
            />
          }
          footer={(() => {
            const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);
            return (
              <Pagination
                page={page}
                pageCount={view.pageCount}
                onPageChange={setPage}
                summary={view.summary}
              />
            );
          })()}
        />
      )}
    </Section>
  );
}
